class CustomMeasureShade extends HTMLElement {
  constructor() {
    super();
    this.step = 1;
    this.totalSteps = 4;
    this.activeMeasure = 'w1';
    this.measurements = {
      width: {
        unit: 'cm',
        values: {
          w1: { number: '', fraction: '0' },
          w2: { number: '', fraction: '0' },
          w3: { number: '', fraction: '0' },
          ow: { number: '', fraction: '0' },
          ol: { number: '', fraction: '0' },
          or: { number: '', fraction: '0' }
        }
      },
      height: {
        unit: 'cm',
        values: {
          h1: { number: '', fraction: '0' },
          h2: { number: '', fraction: '0' },
          h3: { number: '', fraction: '0' },
          oh: { number: '', fraction: '0' },
          ot: { number: '', fraction: '0' }
        }
      }
    };
    this.handleInput = this.handleInput.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.updateViewportHeight = this.updateViewportHeight.bind(this);
    this.suppressFractionToggleUntil = 0;
    this.handleShadeSwiperMediaChange = this.handleShadeSwiperMediaChange.bind(this);
    this.syncVisualImageScale = this.syncVisualImageScale.bind(this);
  }

  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-cmg-step]'));
    this.result = this.querySelector('[data-cmg-result]');
    this.content = this.querySelector('.ctm-mg_cnt');
    this.nextButton = this.querySelector('[data-cmg-next]');
    this.backButton = this.querySelector('[data-cmg-back]');
    this.progress = this.querySelector('[data-cmg-progress]');
    this.stepCount = this.querySelector('[data-cmg-step-count]');
    this.productLabel = this.querySelector('[data-cmg-product-label]');
    this.visualImage = this.querySelector('[data-cmg-visual-image]');
    this.visualPlaceholder = this.querySelector('[data-cmg-visual-placeholder]');
    this.measurementOverlay = this.querySelector('[data-cmg-measurement-overlay]');
    this.liveRegion = this.querySelector('[data-cmg-live]');
    this.resultImage = this.querySelector('[data-cmg-result-image]');
    this.resultWidth = this.querySelector('[data-cmg-result-width]');
    this.resultHeight = this.querySelector('[data-cmg-result-height]');
    this.resultUnit = this.querySelector('[data-cmg-result-unit]');
    this.resultNote = this.querySelector('[data-cmg-result-note]');
    this.resultCta = this.querySelector('[data-cmg-result-cta]');
    this.recalculateButton = this.querySelector('[data-cmg-recalculate]');
    this.error = this.querySelector('[data-cmg-error]');
    this.sectionElement = this.closest('.ctm-mg') || this;
    this.shadeSwiperElement = this.querySelector('[data-cmg-shade-swiper]');
    this.shadeSwiperInstance = null;
    this.shadeSwiperMedia = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
    this.shadeSwiperRetry = null;

    this.totalSteps = this.steps.length || 4;
    this.fileBase = this.dataset.fileBase || '';
    this.filePrefix = this.dataset.filePrefix || '';
    this.fileSeparator = '_';
    this.fileExtension = this.normalizeExtension(this.dataset.fileExtension || 'png');
    this.defaultUnit = this.dataset.defaultUnit === 'in' ? 'in' : 'cm';
    this.defaultResultNote = this.dataset.resultNote || '';
    this.insideResultNote = this.dataset.insideResultNote || this.defaultResultNote;
    this.insideResultNoteCm = this.dataset.insideResultNoteCm || this.insideResultNote;
    this.insideReducedResultNote = this.dataset.insideReducedResultNote || '';
    this.insideReducedResultNoteCm = this.dataset.insideReducedResultNoteCm || this.insideReducedResultNote;
    this.outsideResultNote = this.dataset.outsideResultNote || '';
    this.measurementOverlayBase = this.dataset.measurementOverlayBase || '';

    this.addEventListener('input', this.handleInput);
    this.addEventListener('change', this.handleChange);
    this.addEventListener('focusin', this.handleFocusIn);
    if (this.visualImage) this.visualImage.addEventListener('load', this.syncVisualImageScale);
    document.addEventListener('click', this.handleDocumentClick);
    window.addEventListener('resize', this.updateViewportHeight);
    window.addEventListener('orientationchange', this.updateViewportHeight);
    window.addEventListener('load', this.updateViewportHeight);
    document.addEventListener('shopify:section:load', this.updateViewportHeight);

    if (this.shadeSwiperMedia) {
      if (this.shadeSwiperMedia.addEventListener) {
        this.shadeSwiperMedia.addEventListener('change', this.handleShadeSwiperMediaChange);
      } else if (this.shadeSwiperMedia.addListener) {
        this.shadeSwiperMedia.addListener(this.handleShadeSwiperMediaChange);
      }
    }

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.updateViewportHeight);
      window.visualViewport.addEventListener('scroll', this.updateViewportHeight);
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.goNext());
    }

    if (this.backButton) {
      this.backButton.addEventListener('click', () => this.goBack());
    }

    if (this.recalculateButton) {
      this.recalculateButton.addEventListener('click', () => this.recalculate());
    }

    this.querySelectorAll('[data-cmg-unit]').forEach((button) => {
      button.addEventListener('click', () => {
        this.setUnits(button.dataset.cmgUnit);
      });
    });

    this.querySelectorAll('[data-cmg-option]').forEach((input) => {
      input.addEventListener('keydown', (event) => this.handleOptionKeydown(event, input));
    });

    this.clearMobileDefaultSelections();
    this.enhanceFractionSelects();
    this.syncDefaults();
    this.updateShadeSwiper();
    this.updateViewportHeight();
    this.showStep(1, { focus: false });
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.handleInput);
    this.removeEventListener('change', this.handleChange);
    this.removeEventListener('focusin', this.handleFocusIn);
    if (this.visualImage) this.visualImage.removeEventListener('load', this.syncVisualImageScale);
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('resize', this.updateViewportHeight);
    window.removeEventListener('orientationchange', this.updateViewportHeight);
    window.removeEventListener('load', this.updateViewportHeight);
    document.removeEventListener('shopify:section:load', this.updateViewportHeight);

    if (this.shadeSwiperMedia) {
      if (this.shadeSwiperMedia.removeEventListener) {
        this.shadeSwiperMedia.removeEventListener('change', this.handleShadeSwiperMediaChange);
      } else if (this.shadeSwiperMedia.removeListener) {
        this.shadeSwiperMedia.removeListener(this.handleShadeSwiperMediaChange);
      }
    }

    this.destroyShadeSwiper();
    window.clearTimeout(this.shadeSwiperRetry);

    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.updateViewportHeight);
      window.visualViewport.removeEventListener('scroll', this.updateViewportHeight);
    }
  }

  handleShadeSwiperMediaChange() {
    this.updateShadeSwiper();
  }

  updateShadeSwiper() {
    if (!this.shadeSwiperElement || !this.shadeSwiperMedia) return;

    if (!this.shadeSwiperMedia.matches) {
      this.destroyShadeSwiper();
      return;
    }

    if (this.shadeSwiperInstance) {
      this.shadeSwiperInstance?.slider?.update();
      return;
    }

    if (!window.FoxTheme?.Carousel) {
      window.clearTimeout(this.shadeSwiperRetry);
      this.shadeSwiperRetry = window.setTimeout(() => this.updateShadeSwiper(), 120);
      return;
    }

    this.shadeSwiperInstance = new window.FoxTheme.Carousel(this.shadeSwiperElement, {
      slidesPerView: 'auto',
      spaceBetween: 12,
      freeMode: true,
      grabCursor: true,
      watchSlidesProgress: true,
      a11y: {
        enabled: true
      }
    });
    this.shadeSwiperInstance.init();
  }

  destroyShadeSwiper() {
    window.clearTimeout(this.shadeSwiperRetry);
    this.shadeSwiperInstance?.slider?.destroy(true, true);
    this.shadeSwiperInstance = null;
  }

  enhanceFractionSelects() {
    this.querySelectorAll('[data-cmg-fraction]').forEach((select, index) => {
      if (select.dataset.cmgEnhanced === 'true') return;

      const label = select.labels && select.labels.length ? select.labels[0].textContent.trim() : 'Fraction';
      const labelElement = select.labels && select.labels.length ? select.labels[0] : null;
      const ui = document.createElement('div');
      const button = document.createElement('button');
      const list = document.createElement('div');
      const listId = `${select.id || `cmg-fraction-${index}`}-listbox`;
      const labelId = labelElement?.id || `${select.id || `cmg-fraction-${index}`}-label`;

      if (labelElement && !labelElement.id) labelElement.id = labelId;

      ui.className = 'ctm-mg_frac-ui';
      ui.dataset.cmgFractionUi = select.dataset.cmgFraction;

      button.type = 'button';
      button.className = 'ctm-mg_frac-btn';
      button.setAttribute('role', 'combobox');
      button.setAttribute('aria-haspopup', 'listbox');
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-autocomplete', 'none');
      button.setAttribute('aria-controls', listId);
      if (labelElement) button.setAttribute('aria-labelledby', `${labelId} ${select.id}-value`);
      button.dataset.cmgFractionButton = select.dataset.cmgFraction;

      const buttonValue = document.createElement('span');
      buttonValue.id = `${select.id}-value`;
      button.appendChild(buttonValue);

      list.id = listId;
      list.className = 'ctm-mg_frac-list';
      list.setAttribute('role', 'listbox');
      if (labelElement) {
        list.setAttribute('aria-labelledby', labelId);
      } else {
        list.setAttribute('aria-label', label);
      }
      list.hidden = true;

      Array.from(select.options).forEach((option) => {
        const item = document.createElement('div');
        item.className = 'ctm-mg_frac-option';
        item.textContent = option.textContent;
        item.setAttribute('role', 'option');
        item.dataset.value = option.value;
        item.dataset.cmgFractionOption = select.dataset.cmgFraction;
        list.appendChild(item);
      });

      button.addEventListener('click', (event) => {
        if (button.disabled) return;
        if (Date.now() < this.suppressFractionToggleUntil) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        this.toggleFractionSelect(ui);
      });

      button.addEventListener('keydown', (event) => this.handleFractionButtonKeydown(event, ui));
      list.addEventListener('keydown', (event) => this.handleFractionListKeydown(event, ui));
      list.addEventListener('pointerdown', (event) => {
        const item = event.target.closest('[data-cmg-fraction-option]');
        if (!item) return;
        event.preventDefault();
        event.stopPropagation();
        this.chooseFractionOption(ui, item.dataset.value);
      });
      list.addEventListener('click', (event) => {
        const item = event.target.closest('[data-cmg-fraction-option]');
        if (!item) return;
        event.preventDefault();
        event.stopPropagation();
        if (Date.now() < this.suppressFractionToggleUntil) return;
        this.chooseFractionOption(ui, item.dataset.value);
      });

      ui.append(button, list);
      select.after(ui);
      select.classList.add('ctm-mg_frac--native');
      select.dataset.cmgEnhanced = 'true';
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');
      button.tabIndex = 0;
      this.syncFractionUi(select);
    });
  }

  handleDocumentClick(event) {
    if (event.target.closest('.ctm-mg_frac-ui')) return;
    this.closeFractionSelects();
  }

  handleFractionButtonKeydown(event, ui) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openFractionSelect(ui);
      const selected = ui.querySelector('.ctm-mg_frac-option[aria-selected="true"]');
      const first = ui.querySelector('.ctm-mg_frac-option');
      (selected || first)?.focus();
    }
  }

  handleFractionListKeydown(event, ui) {
    const options = Array.from(ui.querySelectorAll('.ctm-mg_frac-option'));
    const currentIndex = options.indexOf(document.activeElement);

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeFractionSelect(ui, { focus: true });
      return;
    }

    if (event.key === 'Tab') {
      this.closeFractionSelect(ui);
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = (currentIndex + direction + options.length) % options.length;
      options[nextIndex]?.focus();
      return;
    }

    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      options[event.key === 'Home' ? 0 : options.length - 1]?.focus();
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (document.activeElement?.dataset.value) {
        this.chooseFractionOption(ui, document.activeElement.dataset.value, { focus: true });
      }
    }
  }

  toggleFractionSelect(ui) {
    const isOpen = ui.classList.contains('is-open');
    if (isOpen) {
      this.closeFractionSelect(ui, { focus: true });
    } else {
      this.openFractionSelect(ui);
    }
  }

  openFractionSelect(ui) {
    this.closeFractionSelects(ui);
    this.classList.add('is-fraction-open');
    ui.classList.add('is-open');
    ui.querySelector('.ctm-mg_frac-btn')?.setAttribute('aria-expanded', 'true');
    const list = ui.querySelector('.ctm-mg_frac-list');
    if (list) {
      list.hidden = false;
      list.style.removeProperty('display');
    }
    this.positionFractionSelect(ui);
  }

  closeFractionSelect(ui, { focus = false } = {}) {
    ui.classList.remove('is-open');
    ui.classList.remove('is-dropup');
    this.classList.remove('is-fraction-open');
    ui.querySelector('.ctm-mg_frac-btn')?.setAttribute('aria-expanded', 'false');
    const list = ui.querySelector('.ctm-mg_frac-list');
    if (list) {
      list.hidden = true;
      list.style.setProperty('display', 'none', 'important');
      list.style.removeProperty('--cmg-frac-list-max-height');
    }
    if (focus) ui.querySelector('.ctm-mg_frac-btn')?.focus();
  }

  positionFractionSelect(ui) {
    const list = ui.querySelector('.ctm-mg_frac-list');
    if (!list || list.hidden) return;

    if (!window.matchMedia('(max-width: 989px)').matches) {
      ui.classList.remove('is-dropup');
      list.style.removeProperty('--cmg-frac-list-max-height');
      return;
    }
    ui.classList.remove('is-dropup');
    list.style.removeProperty('--cmg-frac-list-max-height');
  }

  closeFractionSelects(except = null) {
    this.querySelectorAll('.ctm-mg_frac-ui.is-open').forEach((ui) => {
      if (ui !== except) this.closeFractionSelect(ui);
    });
  }

  chooseFractionOption(ui, value, { focus = false } = {}) {
    const key = ui.dataset.cmgFractionUi;
    const select = this.querySelector(`[data-cmg-fraction="${key}"]`);
    if (!select) return;

    this.suppressFractionToggleUntil = Date.now() + 700;
    this.closeFractionSelect(ui);
    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    this.syncFractionUi(select);
    if (focus) ui.querySelector('.ctm-mg_frac-btn')?.focus();
  }

  syncFractionUi(select) {
    const ui = select.nextElementSibling?.matches('.ctm-mg_frac-ui') ? select.nextElementSibling : null;
    if (!ui) return;

    const value = select.value || '0';
    const button = ui.querySelector('.ctm-mg_frac-btn');
    const buttonValue = button?.querySelector('span');
    if (buttonValue) buttonValue.textContent = value;
    if (button) button.disabled = select.disabled || select.hidden;
    ui.hidden = select.hidden;

    ui.querySelectorAll('.ctm-mg_frac-option').forEach((item) => {
      item.setAttribute('aria-selected', item.dataset.value === value ? 'true' : 'false');
      item.tabIndex = item.dataset.value === value ? 0 : -1;
    });
  }

  updateViewportHeight() {
    const target = this.sectionElement || this;
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 989px)').matches;
    this.querySelectorAll('.ctm-mg_frac-ui.is-open').forEach((ui) => this.positionFractionSelect(ui));

    if (isMobile) {
      target.style.removeProperty('--cmg-vh');
      this.updateBackgroundFloorHeight(target, 0);
      this.querySelectorAll('.ctm-mg_frac-ui.is-open').forEach((ui) => this.positionFractionSelect(ui));
      return;
    }

    const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    const sectionTop = Math.max(0, target.getBoundingClientRect().top);
    const fixedHeaderHeight = this.getTopOverlayHeight(target);
    const topOffset = Math.max(sectionTop, fixedHeaderHeight);
    const availableHeight = Math.max(360, Math.floor(viewportHeight - topOffset));

    target.style.setProperty('--cmg-vh', `${availableHeight}px`);
    this.updateBackgroundFloorHeight(target, availableHeight);
  }

  updateBackgroundFloorHeight(target, availableHeight) {
    const visual = this.querySelector('.ctm-mg_vis');
    const wall = this.querySelector('.ctm-mg_bg--b');
    const floor = this.querySelector('.ctm-mg_bg--a');

    if (!target || !visual || !wall || !floor) return;

    const wallStyle = getComputedStyle(wall);
    const floorStyle = getComputedStyle(floor);
    const wallImageWidth = parseFloat(wallStyle.getPropertyValue('--cmg-bg-b-image-width'));
    const wallImageHeight = parseFloat(wallStyle.getPropertyValue('--cmg-bg-b-image-height'));
    const floorImageWidth = parseFloat(floorStyle.getPropertyValue('--cmg-bg-a-image-width'));
    const floorImageHeight = parseFloat(floorStyle.getPropertyValue('--cmg-bg-a-image-height'));

    if (!wallImageWidth || !wallImageHeight) return;

    this.wallImageHeight = wallImageHeight;

    const visualRect = visual.getBoundingClientRect();
    const visualWidth = visualRect.width;
    const visualHeight = visualRect.height || availableHeight;
    const isFloorHidden = floorStyle.display === 'none';

    if (isFloorHidden) {
      const scale = Math.max(visualWidth / wallImageWidth, visualHeight / wallImageHeight);
      const renderedWidth = Math.ceil(wallImageWidth * scale);

      target.style.setProperty('--cmg-bg-rendered-width', `${renderedWidth}px`);
      target.style.setProperty('--cmg-bg-b-rendered-height', `${visualHeight}px`);
      target.style.setProperty('--cmg-bg-a-rendered-height', '0px');
      this.wallCoverScale = scale;
      this.syncVisualImageScale();
      return;
    }

    if (!floorImageWidth || !floorImageHeight) return;

    const widthScale = Math.max(visualWidth / wallImageWidth, visualWidth / floorImageWidth);
    const heightScale = visualHeight / (wallImageHeight + floorImageHeight);
    const scale = Math.max(widthScale, heightScale);
    const wallHeight = Math.ceil(wallImageHeight * scale);
    const floorHeight = Math.max(0, Math.ceil(visualHeight - wallHeight));
    const renderedWidth = Math.ceil(Math.max(wallImageWidth, floorImageWidth) * scale);

    target.style.setProperty('--cmg-bg-rendered-width', `${renderedWidth}px`);
    target.style.setProperty('--cmg-bg-b-rendered-height', `${wallHeight}px`);
    target.style.setProperty('--cmg-bg-a-rendered-height', `${floorHeight}px`);
    this.wallCoverScale = Math.max(visualWidth / wallImageWidth, wallHeight / wallImageHeight);
    this.syncVisualImageScale();
  }

  syncVisualImageScale() {
    if (!this.visualImage || !this.sectionElement || !this.wallCoverScale) return;

    const windowImageWidth = this.visualImage.naturalWidth;
    const windowImageHeight = this.visualImage.naturalHeight;
    if (!windowImageWidth || !windowImageHeight || !this.wallImageHeight) return;

    const renderedWallImageHeight = Math.ceil(this.wallImageHeight * this.wallCoverScale);
    const windowScale = renderedWallImageHeight / windowImageHeight;

    this.sectionElement.style.setProperty('--cmg-window-rendered-width', `${Math.ceil(windowImageWidth * windowScale)}px`);
    this.sectionElement.style.setProperty('--cmg-window-rendered-height', `${renderedWallImageHeight}px`);
  }

  getTopOverlayHeight(target) {
    const selectors = [
      '.shopify-section-group-header-group',
      '.header-section',
      '#shopify-section-header',
      'sticky-header',
      'header[is="sticky-header"]'
    ];

    return selectors.reduce((height, selector) => {
      const element = document.querySelector(selector);
      if (!element || element.contains(target)) return height;

      const style = window.getComputedStyle(element);
      if (style.position !== 'fixed' && style.position !== 'sticky') return height;

      const rect = element.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top > 1) return height;

      return Math.max(height, rect.bottom);
    }, 0);
  }

  syncDefaults() {
    let mount = this.getSelectedOption('mount');
    const insideMount = this.querySelector('[data-cmg-option="mount"][data-mount-mode="inside"]');

    if (insideMount && mount !== insideMount) {
      insideMount.checked = true;
      mount = insideMount;
    }

    this.dataset.mountMode = this.getMountMode();
    this.updateShadeOptions();
    this.updateShadeCardsForMount();
    const shade = this.getSelectedOption('shade');

    if (shade && this.productLabel) {
      this.productLabel.textContent = shade.dataset.title || shade.value;
    }

    this.setUnit('width', this.defaultUnit);
    this.setUnit('height', this.defaultUnit);
    this.updateMountPanels();
    this.applyDefaultMeasurementValues();
    this.updateOptionVisual(mount || shade);
  }

  handleInput(event) {
    const input = event.target.closest('[data-cmg-measure-input]');
    if (!input) return;

    const key = input.dataset.cmgMeasureInput;
    const group = this.getMeasureGroup(key);
    if (!group) return;

    const unit = this.measurements[group].unit;
    const value = this.sanitizeMeasurementInput(input, unit);
    this.measurements[group].values[key].number = value;
    this.rememberMeasurementSource(group, key);
    this.setActiveMeasure(key);
    this.updateValidity();
  }

  handleChange(event) {
    const option = event.target.closest('[data-cmg-option]');
    if (option) {
      const previousMountMode = this.dataset.mountMode || this.getMountMode();
      if (option.dataset.cmgOption === 'shade' && this.productLabel) {
        this.productLabel.textContent = option.dataset.title || option.value;
      }
      if (option.dataset.cmgOption === 'mount') {
        this.dataset.mountMode = this.getMountMode();
        if (previousMountMode !== this.dataset.mountMode) {
          this.resetMeasurements(previousMountMode);
        }
        this.updateShadeOptions();
        this.updateShadeCardsForMount();
        this.updateMountPanels();
        this.applyDefaultMeasurementValues();
        this.setDefaultActiveMeasure();
      }
      if (this.step === 1 && option.dataset.cmgOption === 'mount') {
        this.updateOptionVisual(option);
      } else {
        this.updateStepVisual();
      }
      this.updateValidity();
      return;
    }

    const fraction = event.target.closest('[data-cmg-fraction]');
    if (fraction) {
      const key = fraction.dataset.cmgFraction;
      const group = this.getMeasureGroup(key);
      if (!group) return;

      this.measurements[group].values[key].fraction = fraction.value;
      this.rememberMeasurementSource(group, key);
      this.setActiveMeasure(key);
      this.syncFractionUi(fraction);
      this.updateValidity();
    }
  }

  handleFocusIn(event) {
    const row = event.target.closest('[data-cmg-measurement]');
    if (!row) return;

    this.setActiveMeasure(row.dataset.cmgMeasurement);
  }

  handleOptionKeydown(event, input) {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  goNext() {
    if (!this.isStepValid(this.step)) {
      this.showError(this.dataset.requiredMessage || 'Complete this step to continue.');
      return;
    }

    this.clearError();

    if (this.step >= this.totalSteps) {
      this.showResult();
      return;
    }

    this.showStep(this.step + 1);
  }

  goBack() {
    if (this.result && !this.result.hidden) {
      this.showStep(this.totalSteps);
      return;
    }

    if (this.step <= 1) return;

    this.showStep(this.step - 1);
  }

  showStep(step, { focus = true } = {}) {
    this.step = Math.max(1, Math.min(step, this.totalSteps));
    this.dataset.step = String(this.step);
    if (this.content) this.content.scrollTop = 0;

    this.steps.forEach((stepElement) => {
      const isActive = Number(stepElement.dataset.cmgStep) === this.step;
      stepElement.hidden = !isActive;
      stepElement.toggleAttribute('aria-hidden', !isActive);
    });

    if (this.isMobileViewport() && this.isStepWithMeasurementInput(this.getCurrentStepElement())) {
      this.clearActiveMeasure();
    }

    if (this.result) {
      this.result.hidden = true;
      this.result.setAttribute('aria-hidden', 'true');
    }

    if (this.nextButton) {
      this.nextButton.hidden = false;
      this.nextButton.textContent = this.nextButton.dataset.defaultLabel || 'Continue';
    }

    this.updateProgress();
    this.updateBackButton();
    this.updateShadeCardsForMount();
    this.updateMountPanels();
    this.setDefaultActiveMeasure();
    this.updateStepVisual();
    this.updateMeasurementOverlay();
    this.updateValidity();
    this.updateShadeSwiper();
    this.updateViewportHeight();
    this.announce(`Step ${this.step} of ${this.totalSteps}`);

    if (focus) {
      this.focusCurrentStep();
    }
  }

  showResult() {
    const selectedMount = this.getSelectedOption('mount');
    const selectedShade = this.getSelectedOption('shade');
    const result = this.calculateResult();
    const filename = this.buildResultFilename(selectedMount, selectedShade);
    const url = this.buildFileUrl(filename);

    this.steps.forEach((stepElement) => {
      stepElement.hidden = true;
      stepElement.setAttribute('aria-hidden', 'true');
    });

    if (this.result) {
      this.result.hidden = false;
      this.result.removeAttribute('aria-hidden');
    }

    if (this.nextButton) {
      this.nextButton.hidden = true;
    }

    if (this.measurementOverlay) {
      this.measurementOverlay.hidden = true;
      this.measurementOverlay.style.removeProperty('background-image');
    }

    if (this.progress) {
      this.progress.style.setProperty('--cmg-progress', '100%');
      this.progress.setAttribute('aria-valuenow', String(this.totalSteps));
    }

    if (this.stepCount) {
      this.stepCount.textContent = '';
    }

    if (this.resultWidth) {
      this.resultWidth.innerHTML = result.width;
    }

    if (this.resultHeight) {
      this.resultHeight.innerHTML = result.height;
    }

    if (this.resultUnit) {
      this.resultUnit.textContent = this.formatResultUnit(this.measurements.width.unit);
    }

    if (this.resultNote) {
      const isOutside = this.getMountMode() === 'outside';
      if (isOutside) {
        this.resultNote.textContent = this.outsideResultNote;
      } else {
        this.resultNote.textContent = this.getInsideResultNote(selectedShade, this.measurements.width.unit);
      }
    }

    if (this.resultCta) {
      this.resultCta.textContent = this.resultCta.dataset.defaultLabel || 'Shop';
      this.resultCta.href = this.resultCta.dataset.defaultLink || '#';
      this.resultCta.style.setProperty('background-color', 'var(--cmg-accent)', 'important');
      this.resultCta.style.setProperty('color', 'var(--cmg-panel-bg)', 'important');
      this.resultCta.style.setProperty('background-image', 'none', 'important');
      this.resultCta.style.setProperty('box-shadow', 'none', 'important');
      this.resultCta.style.setProperty('filter', 'none', 'important');
      this.resultCta.style.setProperty('text-decoration', 'none', 'important');
    }

    if (this.visualImage && url) {
      this.visualImage.src = url;
      this.visualImage.alt = this.buildResultAlt(selectedMount, selectedShade);
      this.visualImage.hidden = false;
      if (this.visualPlaceholder) this.visualPlaceholder.hidden = true;
    }

    if (this.resultImage && url) {
      this.resultImage.value = filename;
    }

    this.dataset.step = 'result';
    this.updateBackButton();
    this.updateViewportHeight();
    this.announce('Your dimensions are ready.');
    this.focusResult();
  }

  recalculate() {
    this.showStep(1);
  }

  updateProgress() {
    const percent = `${(this.step / this.totalSteps) * 100}%`;

    if (this.progress) {
      this.progress.style.setProperty('--cmg-progress', percent);
      this.progress.setAttribute('aria-valuenow', String(this.step));
      this.progress.setAttribute('aria-valuemax', String(this.totalSteps));
    }

    if (this.stepCount) {
      this.stepCount.textContent = `Step ${this.step} of ${this.totalSteps}`;
    }
  }

  updateBackButton() {
    if (!this.backButton) return;

    const isResult = this.result && !this.result.hidden;
    this.backButton.hidden = this.step <= 1 && !isResult;
  }

  updateStepVisual() {
    const activeStep = this.getCurrentStepElement();

    if (this.step === 1) {
      const selected = this.getSelectedOption('mount');
      this.updateOptionVisual(selected);
      return;
    }

    const image = activeStep ? activeStep.dataset.visualImage : '';

    if (image && this.visualImage) {
      this.visualImage.src = image;
      this.visualImage.alt = activeStep.dataset.visualAlt || '';
      this.visualImage.hidden = false;
      if (this.visualPlaceholder) this.visualPlaceholder.hidden = true;
      return;
    }

    this.clearVisualImage();
  }

  updateOptionVisual(option) {
    if (!option || !option.dataset.visualImage || !this.visualImage) {
      const activeStep = this.getCurrentStepElement();
      const fallback = activeStep ? activeStep.dataset.visualImage : '';
      if (!fallback && this.visualImage) {
        this.clearVisualImage();
      }
      return;
    }

    this.visualImage.src = option.dataset.visualImage;
    this.visualImage.alt = option.dataset.visualAlt || option.dataset.title || '';
    this.visualImage.hidden = false;
    if (this.visualPlaceholder) this.visualPlaceholder.hidden = true;
  }

  clearVisualImage() {
    if (this.visualImage) {
      this.visualImage.removeAttribute('src');
      this.visualImage.alt = '';
      this.visualImage.hidden = true;
    }

    if (this.visualPlaceholder) {
      this.visualPlaceholder.hidden = false;
    }
  }

  updateValidity() {
    if (!this.nextButton || this.result && !this.result.hidden) return;

    this.nextButton.disabled = !this.isStepValid(this.step);
  }

  isStepValid(step) {
    if (step === 1) return Boolean(this.getSelectedOption('mount'));
    if (step === 2) return Boolean(this.getSelectedOption('shade'));
    if (step === 3) return this.areMeasurementsComplete('width');
    if (step === 4) return this.areMeasurementsComplete('height');
    return true;
  }

  areMeasurementsComplete(group) {
    const keys = this.getActiveMeasurementKeys(group);

    return keys.every((key) => {
      const value = this.measurements[group].values[key];
      const number = value ? value.number : '';
      if (number === '') return false;

      return this.isOptionalExtension(key) ? Number(number) >= 0 : Number(number) > 0;
    });
  }

  setUnit(group, unit) {
    if (!this.measurements[group] || !unit) return;

    const nextUnit = unit === 'cm' ? 'cm' : 'in';
    const previousUnit = this.measurements[group].unit;
    const shouldConvert = previousUnit !== nextUnit;

    if (shouldConvert) {
      Object.keys(this.measurements[group].values).forEach((key) => {
        const measurement = this.measurements[group].values[key];
        if (!measurement || measurement.number === '') return;

        if (measurement.sourceUnit === nextUnit) {
          measurement.number = measurement.sourceNumber;
          measurement.fraction = measurement.sourceFraction;
          return;
        }

        const decimalInches = this.getValueInInches(measurement, previousUnit);
        if (nextUnit === 'cm') {
          measurement.number = this.formatNumber(decimalInches * 2.54, 1);
          measurement.fraction = '0';
        } else {
          const wholeAndFraction = this.toWholeAndFraction(decimalInches);
          measurement.number = wholeAndFraction.number;
          measurement.fraction = wholeAndFraction.fraction;
        }

        measurement.number = this.sanitizeMeasurementValue(measurement.number, nextUnit);
      });
    }

    this.measurements[group].unit = nextUnit;
    this.syncMeasurementControls(group);

    this.querySelectorAll(`[data-cmg-unit-group="${group}"]`).forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.cmgUnit === nextUnit ? 'true' : 'false');
    });

    this.querySelectorAll(`[data-cmg-fraction-group="${group}"]`).forEach((select) => {
      select.hidden = nextUnit === 'cm';
      select.disabled = nextUnit === 'cm';
      this.syncFractionUi(select);
    });

    this.querySelectorAll(`[data-cmg-unit-suffix="${group}"]`).forEach((suffix) => {
      suffix.textContent = nextUnit === 'cm' ? 'cm' : 'Inches';
    });

    this.querySelectorAll(`[data-cmg-measure-input]`).forEach((input) => {
      const key = input.dataset.cmgMeasureInput;
      if (this.getMeasureGroup(key) !== group) return;
      input.step = nextUnit === 'cm' ? '0.1' : '1';
      input.inputMode = nextUnit === 'cm' ? 'decimal' : 'numeric';
      input.maxLength = nextUnit === 'cm' ? 5 : 3;
      input.pattern = nextUnit === 'cm' ? '^\\d{0,3}(\\.\\d?)?$' : '^\\d{0,3}$';
      input.value = this.sanitizeMeasurementValue(input.value, nextUnit);
    });

    this.updateValidity();
  }

  setUnits(unit) {
    const nextUnit = unit === 'cm' ? 'cm' : 'in';
    Object.keys(this.measurements).forEach((group) => {
      this.setUnit(group, nextUnit);
    });
  }

  rememberMeasurementSource(group, key) {
    const measurement = this.measurements[group] && this.measurements[group].values[key];
    if (!measurement) return;

    measurement.sourceUnit = this.measurements[group].unit;
    measurement.sourceNumber = measurement.number;
    measurement.sourceFraction = this.measurements[group].unit === 'cm' ? '0' : measurement.fraction;
  }

  sanitizeMeasurementInput(input, unit) {
    const value = this.sanitizeMeasurementValue(input.value, unit);
    if (input.value !== value) input.value = value;
    return value;
  }

  sanitizeMeasurementValue(value, unit) {
    const normalized = String(value || '').replace(',', '.').replace(/[^\d.]/g, '');
    if (!normalized) return '';

    const parts = normalized.split('.');
    const whole = parts.shift().slice(0, 3);

    if (unit !== 'cm') return whole;
    if (!parts.length) return whole;
    if (!whole) return '';

    const decimal = parts.join('').slice(0, 1);
    return `${whole}.${decimal}`;
  }

  syncMeasurementControls(group) {
    const values = this.measurements[group] && this.measurements[group].values;
    if (!values) return;

    Object.keys(values).forEach((key) => {
      const measurement = values[key];

      this.querySelectorAll(`[data-cmg-measure-input="${key}"]`).forEach((input) => {
        input.value = measurement.number;
      });

      this.querySelectorAll(`[data-cmg-fraction="${key}"]`).forEach((select) => {
        select.value = measurement.fraction;
        this.syncFractionUi(select);
      });
    });
  }

  setActiveMeasure(key) {
    if (!key) return;

    this.activeMeasure = key;
    this.dataset.activeMeasure = key;

    this.querySelectorAll('[data-cmg-measurement]').forEach((row) => {
      row.classList.toggle('is-active', row.dataset.cmgMeasurement === key);
    });

    this.updateMeasurementOverlay();
  }

  clearActiveMeasure() {
    this.activeMeasure = '';
    this.removeAttribute('data-active-measure');
    this.querySelectorAll('[data-cmg-measurement]').forEach((row) => {
      row.classList.remove('is-active');
    });
    this.updateMeasurementOverlay();
  }

  updateMeasurementOverlay() {
    if (!this.measurementOverlay) return;

    const url = this.getMeasurementOverlayUrl();
    if (!url) {
      this.measurementOverlay.hidden = true;
      this.measurementOverlay.style.removeProperty('background-image');
      return;
    }

    this.measurementOverlay.style.setProperty('background-image', `url("${url}")`);
    this.measurementOverlay.hidden = false;
  }

  getMeasurementOverlayUrl() {
    if (!this.measurementOverlayBase || this.step < 3 || this.step > 4) return '';

    const mountSegment = this.getMountMode() === 'outside' ? 'out' : 'in';
    const measurementSegment = this.getMeasurementOverlaySegment(this.activeMeasure);
    if (!measurementSegment) return '';

    const fileBase = `shade-${mountSegment}-${measurementSegment.toUpperCase()}`;
    return this.measurementOverlayBase
      .replace('__CMG_MEASUREMENT_OVERLAY__.png', `${fileBase}.png`)
      .replace('__CMG_MEASUREMENT_OVERLAY__', fileBase);
  }

  getMeasurementOverlaySegment(key) {
    if (this.getMountMode() !== 'outside') return key;

    const outsideMap = {
      ow: 'w1',
      ol: 'w2',
      or: 'w3',
      oh: 'h1',
      ot: 'h2'
    };

    return outsideMap[key] || '';
  }

  setDefaultActiveMeasure() {
    if (this.isMobileViewport() && this.isStepWithMeasurementInput(this.getCurrentStepElement())) {
      this.clearActiveMeasure();
      return;
    }

    if (this.step === 3) {
      this.setActiveMeasure(this.getMountMode() === 'outside' ? 'ow' : 'w1');
    }

    if (this.step === 4) {
      this.setActiveMeasure(this.getMountMode() === 'outside' ? 'oh' : 'h1');
    }
  }

  updateMountPanels() {
    const mode = this.getMountMode();
    this.dataset.mountMode = mode;

    this.querySelectorAll('[data-cmg-mount-panel]').forEach((panel) => {
      const isActive = panel.dataset.cmgMountPanel === mode;
      panel.hidden = !isActive;
      panel.toggleAttribute('aria-hidden', !isActive);
      panel.querySelectorAll('input, select, button').forEach((control) => {
        const fractionGroup = control.dataset.cmgFractionGroup;
        const isCmFraction = fractionGroup && this.measurements[fractionGroup] && this.measurements[fractionGroup].unit === 'cm';
        control.disabled = !isActive || Boolean(isCmFraction);
      });
    });

    this.updateStepDescriptions();
  }

  updateShadeOptions() {
    const isOutsideMount = this.getMountMode() === 'outside';
    const shadeOptions = Array.from(this.querySelectorAll('[data-cmg-option="shade"]'));
    let selectedShade = this.getSelectedOption('shade');

    shadeOptions.forEach((input) => {
      const shouldHide = isOutsideMount && this.isOutsideExcludedShade(input);
      const card = input.closest('.ctm-mg_chc');

      input.disabled = shouldHide;
      if (shouldHide && input.checked) {
        input.checked = false;
        selectedShade = null;
      }

      if (card) {
        card.hidden = shouldHide;
        card.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
        card.classList.toggle('is-mount-hidden', shouldHide);
        card.style.display = shouldHide ? 'none' : '';
      }
    });

    if (!selectedShade || selectedShade.disabled) {
      const firstAvailableShade = shadeOptions.find((input) => !input.disabled);
      if (firstAvailableShade) {
        firstAvailableShade.checked = true;
        selectedShade = firstAvailableShade;
      }
    }

    if (selectedShade && this.productLabel) {
      this.productLabel.textContent = selectedShade.dataset.title || selectedShade.value;
    }

    this.updateShadeCardsForMount();
    this.updateShadeSwiper();
    this.shadeSwiperInstance?.slider?.update?.();
  }

  updateShadeCardsForMount() {
    const mode = this.getMountMode();

    this.querySelectorAll('[data-cmg-option="shade"]').forEach((input) => {
      const card = input.closest('.ctm-mg_chc');
      if (!card) return;

      const description = card.querySelector('[data-cmg-shade-description]');
      const image = card.querySelector('[data-cmg-shade-image]');
      const placeholder = card.querySelector('[data-cmg-shade-placeholder]');
      const nextDescription = mode === 'outside'
        ? input.dataset.outsideDescription
        : input.dataset.insideDescription;
      const nextImage = mode === 'outside'
        ? input.dataset.outsideImage
        : input.dataset.insideImage;
      const nextImageSrcset = mode === 'outside'
        ? input.dataset.outsideImageSrcset
        : input.dataset.insideImageSrcset;
      const nextAlt = mode === 'outside'
        ? input.dataset.outsideImageAlt
        : input.dataset.insideImageAlt;

      if (description) {
        description.textContent = nextDescription || '';
        description.hidden = !nextDescription;
      }

      if (!image) {
        if (placeholder) placeholder.hidden = Boolean(nextImage);
        return;
      }

      if (nextImage) {
        image.src = nextImage;
        if (nextImageSrcset) {
          image.srcset = nextImageSrcset;
        } else {
          image.removeAttribute('srcset');
        }
        image.alt = nextAlt || input.dataset.title || '';
        image.hidden = false;
        if (placeholder) placeholder.hidden = true;
      } else {
        image.removeAttribute('src');
        image.removeAttribute('srcset');
        image.alt = '';
        image.hidden = true;
        if (placeholder) placeholder.hidden = false;
      }
    });
  }

  applyDefaultMeasurementValues() {
    const activeKeys = ['ol', 'or', 'ot'];

    activeKeys.forEach((key) => {
      const input = this.querySelector(`[data-cmg-measure-input="${key}"]`);
      const group = this.getMeasureGroup(key);
      const measurement = this.measurements[group] && this.measurements[group].values[key];
      if (!input || !group || !measurement) return;
      if (this.getMountMode() !== 'outside') return;
      if (measurement.number !== '') return;

      const defaultValue = this.sanitizeMeasurementValue(input.dataset.cmgDefaultValue || '', this.measurements[group].unit);
      if (defaultValue === '') return;

      measurement.number = defaultValue;
      measurement.fraction = '0';
      input.value = defaultValue;
      this.rememberMeasurementSource(group, key);

      this.querySelectorAll(`[data-cmg-fraction="${key}"]`).forEach((select) => {
        select.value = '0';
        this.syncFractionUi(select);
      });
    });

    this.updateValidity();
  }

  isOutsideExcludedShade(shadeOption) {
    if (!shadeOption) return false;

    const title = this.cleanSegment(shadeOption.dataset.title);
    const segment = this.cleanSegment(shadeOption.dataset.filenameSegment);
    return title.includes('no-drill') || segment.includes('no-drill');
  }

  resetMeasurements(mode) {
    const keys = mode === 'outside' ? ['ow', 'ol', 'or', 'oh', 'ot'] : ['w1', 'w2', 'w3', 'h1', 'h2', 'h3'];

    keys.forEach((key) => {
      const group = this.getMeasureGroup(key);
      const value = this.measurements[group] && this.measurements[group].values[key];
      if (!value) return;

      value.number = '';
      value.fraction = '0';

      this.querySelectorAll(`[data-cmg-measure-input="${key}"]`).forEach((input) => {
        input.value = '';
      });
      this.querySelectorAll(`[data-cmg-fraction="${key}"]`).forEach((select) => {
        select.value = '0';
        this.syncFractionUi(select);
      });
    });
  }

  calculateResult() {
    const widthUnit = this.measurements.width.unit;
    const heightUnit = this.measurements.height.unit;
    const mountMode = this.getMountMode();
    const selectedShade = this.getSelectedOption('shade');
    let width;
    let height;

    if (mountMode === 'outside') {
      width = ['ow', 'ol', 'or'].reduce((total, key) => {
        return total + this.toDecimal(this.measurements.width.values[key], widthUnit);
      }, 0);
      height = ['oh', 'ot'].reduce((total, key) => {
        return total + this.toDecimal(this.measurements.height.values[key], heightUnit);
      }, 0);
    } else {
      const widthValues = ['w1', 'w2', 'w3'].map((key) => this.toDecimal(this.measurements.width.values[key], widthUnit));
      const heightValues = ['h1', 'h2', 'h3'].map((key) => this.toDecimal(this.measurements.height.values[key], heightUnit));
      width = Math.max(0, Math.min(...widthValues) - this.getInsideWidthDeduction(selectedShade, widthUnit));
      height = Math.max(...heightValues);
    }

    return {
      width: this.formatMeasurement(width, widthUnit),
      height: this.formatMeasurement(height, heightUnit)
    };
  }

  toDecimal(value, unit) {
    const number = Number(value.number || 0);
    if (unit === 'cm') return number;

    return number + this.fractionToDecimal(value.fraction);
  }

  getValueInInches(value, unit) {
    const number = Number(value.number || 0);
    if (unit === 'cm') return number / 2.54;

    return number + this.fractionToDecimal(value.fraction);
  }

  toWholeAndFraction(value) {
    let whole = Math.floor(Math.max(0, value));
    let fraction = this.decimalToFraction(value - whole);
    if (fraction === '1') {
      whole += 1;
      fraction = '0';
    }

    return { number: String(whole), fraction };
  }

  formatNumber(value, precision = 1) {
    return Number(value.toFixed(precision)).toString();
  }

  fractionToDecimal(fraction) {
    if (!fraction || fraction === '0') return 0;

    const parts = fraction.split('/');
    if (parts.length !== 2) return 0;

    const numerator = Number(parts[0]);
    const denominator = Number(parts[1]);
    if (!denominator) return 0;

    return numerator / denominator;
  }

  formatMeasurement(value, unit) {
    if (unit === 'cm') {
      return this.formatMeasurementMarkup(this.formatNumber(value, 1));
    }

    const wholeAndFraction = this.toWholeAndFraction(value);
    const fraction = this.formatDisplayFraction(wholeAndFraction.fraction);
    const valueLabel = wholeAndFraction.fraction === '0'
      ? wholeAndFraction.number
      : `${wholeAndFraction.number} ${fraction}`;

    return this.formatMeasurementMarkup(valueLabel);
  }

  formatDisplayFraction(fraction) {
    const fractionGlyphs = {
      '1/8': '⅛',
      '1/4': '¼',
      '3/8': '⅜',
      '1/2': '½',
      '5/8': '⅝',
      '3/4': '¾',
      '7/8': '⅞'
    };
    return fractionGlyphs[fraction] || fraction;
  }

  formatMeasurementMarkup(value) {
    return `<span>${value}</span>`;
  }

  formatResultUnit(unit) {
    return unit === 'cm' ? '(cm)' : '(Inches)';
  }

  decimalToFraction(decimal) {
    const eighths = Math.round(decimal * 8);
    const map = {
      0: '0',
      1: '1/8',
      2: '1/4',
      3: '3/8',
      4: '1/2',
      5: '5/8',
      6: '3/4',
      7: '7/8',
      8: '1'
    };
    return map[eighths] || '0';
  }

  buildResultFilename(mount, shade) {
    const segments = [this.filePrefix, mount ? mount.dataset.filenameSegment : '', shade ? shade.dataset.filenameSegment : '']
      .map((segment) => this.cleanFilenameSegment(segment))
      .filter(Boolean);

    return `${segments.join(this.fileSeparator)}.${this.fileExtension}`;
  }

  buildFileUrl(filename) {
    if (!filename) return '';
    if (!this.fileBase) return filename;

    return this.fileBase.replace('__CMG_FILE__', encodeURIComponent(filename));
  }

  buildResultAlt(mount, shade) {
    const mountTitle = mount ? mount.dataset.title : '';
    const shadeTitle = shade ? shade.dataset.title : '';
    return [mountTitle, shadeTitle, 'measurement result'].filter(Boolean).join(' ');
  }

  getInsideWidthDeduction(shade, unit) {
    const deduction = this.isReducedDeductionShade(shade) ? 0.25 : 0.375;
    return unit === 'cm' ? deduction * 2.54 : deduction;
  }

  getInsideResultNote(shade, unit = this.measurements.width.unit) {
    if (this.isReducedDeductionShade(shade)) {
      return unit === 'cm' ? this.insideReducedResultNoteCm : this.insideReducedResultNote;
    }

    return unit === 'cm' ? this.insideResultNoteCm : this.insideResultNote;
  }

  isReducedDeductionShade(shade) {
    if (!shade) return false;

    const title = this.cleanSegment(shade.dataset.title);
    const segment = this.cleanSegment(shade.dataset.filenameSegment);
    const haystack = `${title} ${segment}`;
    return haystack.includes('cellular') || haystack.includes('venetian');
  }

  cleanFilenameSegment(segment) {
    return String(segment || '').trim();
  }

  cleanSegment(segment) {
    return String(segment || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  normalizeExtension(extension) {
    return String(extension || 'png').replace(/^\./, '').trim() || 'png';
  }

  getSelectedOption(type) {
    return this.querySelector(`[data-cmg-option="${type}"]:checked`);
  }

  isMobileViewport() {
    return window.matchMedia?.('(max-width: 989px)').matches === true;
  }

  isStepWithMeasurementInput(stepElement) {
    return Boolean(stepElement?.querySelector('[data-cmg-measure-input]'));
  }

  clearMobileDefaultSelections() {
    if (!this.isMobileViewport()) return;

    this.querySelectorAll('[data-cmg-option]').forEach((option) => {
      if (this.isStepWithMeasurementInput(option.closest('[data-cmg-step]'))) option.checked = false;
    });
  }

  getMountMode() {
    const mount = this.getSelectedOption('mount');
    return mount && mount.dataset.mountMode === 'outside' ? 'outside' : 'inside';
  }

  updateStepDescriptions() {
    const step = this.getCurrentStepElement();
    if (!step) return;

    const description = step.querySelector('[data-cmg-step-description]');
    if (!description) return;

    if (!['3', '4'].includes(String(step.dataset.cmgStep))) return;

    const title = step.querySelector('[data-cmg-step-title]');
    const nextText = this.getMountMode() === 'outside'
      ? (step.dataset.outsideText || '')
      : (step.dataset.insideText || '');
    const hasDescription = Boolean(nextText.trim());

    description.textContent = nextText;
    description.hidden = !hasDescription;
    description.setAttribute('aria-hidden', hasDescription ? 'false' : 'true');
    title?.classList.toggle('ctm-mg_hd--no-description', !hasDescription);
  }

  getActiveMeasurementKeys(group) {
    const isOutside = this.getMountMode() === 'outside';
    if (group === 'width') return isOutside ? ['ow', 'ol', 'or'] : ['w1', 'w2', 'w3'];
    if (group === 'height') return isOutside ? ['oh', 'ot'] : ['h1', 'h2', 'h3'];
    return [];
  }

  isOptionalExtension(key) {
    return ['ol', 'or', 'ot'].includes(key);
  }

  getMeasureGroup(key) {
    if (!key) return null;
    if (['w', 'o'].includes(key.charAt(0))) {
      return key === 'oh' || key === 'ot' ? 'height' : 'width';
    }
    return key.charAt(0) === 'h' ? 'height' : 'width';
  }

  getCurrentStepElement() {
    return this.querySelector(`[data-cmg-step="${this.step}"]`);
  }

  focusCurrentStep() {
    if (this.isMobileViewport()) return;

    const activeStep = this.getCurrentStepElement();
    if (!activeStep) return;

    const target = this.getActiveMeasureInput(activeStep) || activeStep.querySelector('[data-cmg-step-title]') || activeStep.querySelector('input, button, select, a');
    if (target) {
      target.focus({ preventScroll: true });
    }
  }

  getActiveMeasureInput(scope = this) {
    if (this.step !== 3 && this.step !== 4) return null;

    return scope.querySelector(`.ctm-mg_msrm.is-active [data-cmg-measure-input="${this.activeMeasure}"]`)
      || scope.querySelector('.ctm-mg_msrm.is-active [data-cmg-measure-input]')
      || scope.querySelector('[data-cmg-measure-input]');
  }

  focusResult() {
    const heading = this.result ? this.result.querySelector('[data-cmg-result-title]') : null;
    if (heading) {
      heading.focus({ preventScroll: true });
    }
  }

  showError(message) {
    if (this.error) {
      this.error.textContent = message;
    }
    this.announce(message);
  }

  clearError() {
    if (this.error) {
      this.error.textContent = '';
    }
  }

  announce(message) {
    if (!this.liveRegion) return;

    this.liveRegion.textContent = '';
    window.setTimeout(() => {
      this.liveRegion.textContent = message;
    }, 20);
  }
}

if (!customElements.get('custom-measure-shade')) {
  customElements.define('custom-measure-shade', CustomMeasureShade);
}
